import React, {useState, useEffect, useCallback} from "react";
import {useNavigate} from "react-router-dom";
import {useAuth} from "../contexts/AuthContext";
import {isSignInWithEmailLink} from "firebase/auth";
import {auth} from "../firebase";
import "../App.css";

const Login: React.FC = () => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const {sendLoginLink, verifyEmailLink, user} = useAuth();
  const navigate = useNavigate();

  const handleEmailLinkSignIn = useCallback(async (emailAddress: string) => {
    setLoading(true);
    setError("");
    try {
      await verifyEmailLink(emailAddress, window.location.href);
      navigate("/dashboard");
    } catch (err: any) {
      setError(err.message || "Failed to sign in with email link");
      window.localStorage.removeItem("emailForSignIn");
    } finally {
      setLoading(false);
    }
  }, [verifyEmailLink, navigate]);

  // Check if this is a return from email link
  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    } else if (auth && isSignInWithEmailLink(auth, window.location.href)) {
      // We're handling the email link - email should be in localStorage
      const emailForSignIn = window.localStorage.getItem("emailForSignIn");
      if (emailForSignIn) {
        setEmail(emailForSignIn);
        handleEmailLinkSignIn(emailForSignIn);
      } else {
        setError("Please enter your email to complete sign-in");
      }
    }
  }, [user, navigate, handleEmailLinkSignIn]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    
    // Validate email domain restriction
    const allowedEmails = [
      "miguel.perez@adevinta.com",
      "diego.duchowney@adevinta.com"
    ];
    const emailLower = email.toLowerCase().trim();
    const isAllowed = allowedEmails.some(allowed => 
      emailLower === allowed.toLowerCase()
    );
    
    if (!isAllowed) {
      setError(`Access restricted. Only authorized email addresses are allowed.`);
      return;
    }

    setLoading(true);

    try {
      await sendLoginLink(email.trim());
      setLinkSent(true);
    } catch (err: any) {
      setError(err.message || "Failed to send login link");
    } finally {
      setLoading(false);
    }
  };

  if (linkSent) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-card">
            <div className="login-header">
              <div className="login-icon success-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.7088 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h1 className="login-title">Check Your Email</h1>
              <p className="login-subtitle">
                We've sent a sign-in link to
              </p>
              <p className="login-email">{email}</p>
            </div>

            <div className="alert alert-success" style={{marginBottom: "1.5rem"}}>
              <div style={{display: "flex", alignItems: "flex-start", gap: "0.75rem"}}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.7088 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div>
                  <strong>Link sent successfully!</strong>
                  <p style={{margin: "0.5rem 0 0 0", fontSize: "0.875rem"}}>
                    Click the link in your email to sign in. The link will expire in 1 hour.
                  </p>
                </div>
              </div>
            </div>

            <div style={{marginBottom: "1.5rem", padding: "1rem", backgroundColor: "#f8f9fa", borderRadius: "8px", fontSize: "0.875rem", color: "#666"}}>
              <p style={{margin: 0, display: "flex", alignItems: "center", gap: "0.5rem"}}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Don't see the email? Check your spam folder.
              </p>
            </div>

            <button
              className="btn btn-secondary"
              style={{width: "100%"}}
              onClick={() => {
                setLinkSent(false);
                setEmail("");
              }}
            >
              Send Another Link
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="login-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M20.59 22C20.59 18.13 16.74 15 12 15C7.26 15 3.41 18.13 3.41 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h1 className="login-title">Welcome Back</h1>
            <p className="login-subtitle">
              Enter your email to receive a secure sign-in link
            </p>
          </div>

          {error && (
            <div className="alert alert-error" style={{marginBottom: "1.5rem"}}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label className="form-label" htmlFor="email">
                Email Address
              </label>
              <div className="input-wrapper">
                <svg className="input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <input
                  id="email"
                  type="email"
                  className="form-input login-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  required
                  disabled={loading}
                  autoFocus
                />
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary login-button"
              disabled={loading || !email}
            >
              {loading ? (
                <>
                  <span className="button-spinner"></span>
                  Sending...
                </>
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Send Sign-In Link
                </>
              )}
            </button>
          </form>

          <div className="login-footer">
            <div className="login-info">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <line x1="12" y1="16" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <line x1="12" y1="8" x2="12.01" y2="8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span>Link expires in 1 hour • No password required</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;

